import { z } from "zod";

import { VibeGitProjectStatusReadSchema } from "./status-read.schemas";

/**
 * Slim summary of the git project attached to an app — enough for the
 * console's Code tab to decide whether to render the code browser (READY),
 * a still-provisioning state (PENDING / FAILED), or the attach-a-project CTA
 * (the field is `null` on the envelope). The full project detail
 * (defaultBranch, s3Prefix, gitRemoteUrl, …) stays on the
 * `/git-projects/:id` read; `id` here is what addresses the tree/blob/
 * branches endpoints, which are keyed by git-project id, not app id.
 */
export const VibeAppGitProjectSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: VibeGitProjectStatusReadSchema
});

export type VibeAppGitProjectSummaryDto = z.infer<typeof VibeAppGitProjectSummarySchema>;

/**
 * The person who created an app, summarized to what a console column can
 * render. Exactly the shape `AccessCardCreatorSchema` and
 * `CredentialCreatorSchema` already publish — an owner is an owner, and three
 * spellings of one summary is how a "Created by" column starts disagreeing
 * with itself across screens.
 *
 * Deliberately NOT the whole user: an org-wide list read has no business
 * carrying every creator's email or auth identifiers.
 *
 * Both name parts are nullable INDEPENDENTLY of the object, and that is not
 * defensive typing — `User.firstName` / `User.lastName` are nullable columns,
 * so a real member with neither is representable and reaches this shape as
 * `{ id, firstName: null, lastName: null }`. The display name is composed
 * client-side; the server never fabricates one, because a fabricated name is
 * indistinguishable from a real one.
 */
export const VibeAppCreatorSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable()
});

export type VibeAppCreatorDto = z.infer<typeof VibeAppCreatorSchema>;
